import React, { useState } from 'react'
import { 
  FiGrid, 
  FiList, 
  FiSearch,
  FiImage,
  FiTrash2,
  FiExternalLink,
  FiHardDrive,
  FiRefreshCw,
  FiVideo,
  FiPlay,
  FiEye
} from 'react-icons/fi'
import { useMedia } from '../../hooks/useMedia'
import { MediaFile, MediaViewMode } from '../../types/media'
import ImageUpload from './ImageUpload'
import VideoUpload from './VideoUpload'

const MediaLibrary: React.FC = () => {
  const { mediaFiles, loading, deleteFile, syncWithStorage } = useMedia()
  const [viewMode, setViewMode] = useState<MediaViewMode>('grid')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<string[]>([])
  const [showUpload, setShowUpload] = useState(false)
  const [uploadType, setUploadType] = useState<'image' | 'video'>('image')
  const [syncing, setSyncing] = useState(false)

  // Safe default to empty array if mediaFiles is undefined
  const safeMediaFiles = mediaFiles || []

  const filteredFiles = safeMediaFiles.filter(file => 
    file.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    file.originalName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (file.tags && file.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
  )

  const stats = {
    total: safeMediaFiles.length,
    images: safeMediaFiles.filter(f => f.type === 'image').length,
    videos: safeMediaFiles.filter(f => f.type === 'video').length,
    totalSize: safeMediaFiles.reduce((acc, file) => acc + (file.size || 0), 0)
  }

  const handleSelectFile = (fileId: string) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    )
  }

  const handleSelectAll = () => {
    if (selectedFiles.length === filteredFiles.length) {
      setSelectedFiles([])
    } else {
      setSelectedFiles(filteredFiles.map(file => file.id))
    }
  }

  const handleDeleteFile = async (file: MediaFile) => {
    if (window.confirm(`Are you sure you want to delete "${file.originalName}"?`)) {
      await deleteFile(file)
    }
  }

  const handleSync = async () => {
    setSyncing(true)
    await syncWithStorage()
    setSyncing(false)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-neutral-600">Loading media library...</span>
      </div>
    )
  }

  // Show error state if there's an issue
  if (!mediaFiles) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-red-600 mb-4">Error loading media files</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          Reload Page
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 relative">
      {/* Background Gradient Flares */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-gradient-to-br from-purple-200/80 to-blue-200/80 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      <div className="absolute bottom-20 right-1/4 w-[500px] h-[500px] bg-gradient-to-tl from-blue-200/65 to-purple-200/65 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 mb-1">Media Library</h1>
          <p className="text-neutral-600 text-sm">
            Upload and manage images and videos for your artists and content
          </p>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => {
              setUploadType('image')
              setShowUpload(!showUpload)
            }}
            className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium px-4 py-2.5 rounded-lg hover:shadow-md transition-all text-sm flex-1 sm:flex-initial"
          >
            <FiImage size={18} />
            <span>{showUpload && uploadType === 'image' ? 'Hide' : 'Upload Images'}</span>
          </button>
          <button
            onClick={() => {
              setUploadType('video')
              setShowUpload(!showUpload)
            }}
            className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium px-4 py-2.5 rounded-lg hover:shadow-md transition-all text-sm flex-1 sm:flex-initial"
          >
            <FiVideo size={18} />
            <span>{showUpload && uploadType === 'video' ? 'Hide' : 'Upload Videos'}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Files', value: stats.total, icon: FiHardDrive, gradient: 'from-blue-500/50 to-purple-500/50', tooltip: 'Total media files in library' },
          { label: 'Images', value: stats.images, icon: FiImage, gradient: 'from-purple-500/50 to-indigo-500/50', tooltip: 'Image files' },
          { label: 'Videos', value: stats.videos, icon: FiVideo, gradient: 'from-indigo-500/50 to-purple-500/50', tooltip: 'Video files' },
          { label: 'Storage Used', value: formatFileSize(stats.totalSize), icon: FiHardDrive, gradient: 'from-purple-500/50 to-blue-500/50', tooltip: 'Total storage space used' },
        ].map((stat, index) => (
          <div
            key={index}
            className={`p-[1px] bg-gradient-to-br ${stat.gradient} rounded-lg hover:shadow-lg transition-all`}
          >
            <div 
              className="bg-white rounded-lg p-5 hover:bg-gradient-to-br hover:from-white hover:to-neutral-50 transition-all"
              title={stat.tooltip}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-neutral-50 rounded-md">
                  <stat.icon size={18} className="text-neutral-700" />
                </div>
              </div>
              <div>
                <p className="text-2xl font-semibold text-neutral-900 mb-1">{stat.value}</p>
                <p className="text-sm text-neutral-600">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Upload Section */}
      {showUpload && (
        <div className="p-[1px] bg-gradient-to-r from-blue-500/50 to-indigo-500/50 rounded-lg">
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-neutral-900">
                Upload New {uploadType === 'image' ? 'Images' : 'Videos'}
              </h2>
              <div className="flex items-center bg-neutral-100 rounded-lg p-1">
                <button
                  onClick={() => setUploadType('image')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-all text-sm ${
                    uploadType === 'image'
                      ? 'bg-white text-neutral-900 shadow-sm font-medium'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  <FiImage size={16} />
                  <span>Images</span>
                </button>
                <button
                  onClick={() => setUploadType('video')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-all text-sm ${
                    uploadType === 'video'
                      ? 'bg-white text-neutral-900 shadow-sm font-medium'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  <FiVideo size={16} />
                  <span>Videos</span>
                </button>
              </div>
            </div>
            
            {uploadType === 'image' ? (
              <ImageUpload
                onUploadComplete={(urls) => {
                  console.log('Uploaded:', urls)
                  setShowUpload(false)
                }}
                maxFiles={10}
              />
            ) : (
              <VideoUpload
                onUploadComplete={(videoUrl) => {
                  console.log('Uploaded:', videoUrl)
                  setShowUpload(false)
                }}
                maxFiles={1}
              />
            )}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="p-[1px] bg-gradient-to-r from-purple-500/50 via-indigo-500/50 to-blue-500/50 rounded-lg">
        <div className="bg-white rounded-lg p-5">
        <div className="flex flex-col gap-4">
          {/* Search and View Toggle Row */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
                <input
                  type="text"
                  placeholder="Search files by name or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                />
              </div>
            </div>

            {/* View Mode Toggle and Sync */}
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-neutral-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-md transition-all text-sm flex-1 sm:flex-initial ${
                    viewMode === 'grid'
                      ? 'bg-white text-neutral-900 shadow-sm font-medium'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  <FiGrid size={16} />
                  <span>Grid</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-md transition-all text-sm flex-1 sm:flex-initial ${
                    viewMode === 'list'
                      ? 'bg-white text-neutral-900 shadow-sm font-medium'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  <FiList size={16} />
                  <span>List</span>
                </button>
              </div>
              
              {/* Sync Button */}
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center space-x-2 px-3 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                title="Sync with Storage - removes orphaned records"
              >
                <FiRefreshCw className={syncing ? 'animate-spin' : ''} size={16} />
                <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Sync'}</span>
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedFiles.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-neutral-50 border border-neutral-200 rounded-lg">
              <span className="text-sm text-neutral-600">
                {selectedFiles.length} selected
              </span>
              <button className="text-sm px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors w-full sm:w-auto">
                Delete Selected
              </button>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Media Grid/List */}
      <div className="p-[1px] bg-gradient-to-br from-indigo-500/50 to-purple-500/50 rounded-lg">
        <div className="bg-white rounded-lg">
        <div className="p-5 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-neutral-900">
              Media Files ({filteredFiles.length})
            </h2>
            {filteredFiles.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-purple-600 transition-colors"
              >
                {selectedFiles.length === filteredFiles.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
        </div>

        {filteredFiles.length === 0 ? (
          <div className="p-12 text-center">
            <FiImage size={48} className="text-neutral-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-800 mb-2">
              {searchQuery ? 'No files found' : 'No media files yet'}
            </h3>
            <p className="text-neutral-600 mb-6 text-sm">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Upload your first images to get started'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowUpload(true)}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium px-5 py-2.5 rounded-lg hover:shadow-md transition-all text-sm"
              >
                <FiImage size={18} />
                <span>Upload Images</span>
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {filteredFiles.map((file) => (
                <div
                  key={file.id}
                  className="group relative bg-neutral-50 rounded-lg overflow-hidden hover:shadow-lg transition-all border border-neutral-200"
                >
                  {/* Checkbox */}
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.id)}
                      onChange={() => handleSelectFile(file.id)}
                      className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                  </div>

                  {/* Media Preview */}
                  <div className="aspect-square relative">
                    {file.type === 'video' ? (
                      <>
                        <video
                          src={file.url}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                          <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                            <FiPlay className="text-neutral-800 ml-1" size={20} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <img
                        src={file.url}
                        alt={file.altText || file.originalName}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex space-x-2">
                      <button
                        onClick={() => window.open(file.url, '_blank')}
                        className="p-2 bg-white text-neutral-800 rounded-md hover:bg-neutral-100 transition-colors"
                        title="View full size"
                      >
                        <FiEye size={16} />
                      </button>
                      <button
                        onClick={() => navigator.clipboard.writeText(file.url)}
                        className="p-2 bg-white text-neutral-800 rounded-md hover:bg-neutral-100 transition-colors"
                        title="Copy URL"
                      >
                        <FiExternalLink size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteFile(file)}
                        className="p-2 bg-white text-red-600 rounded-md hover:bg-red-50 transition-colors"
                        title="Delete file"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* File Info */}
                  <div className="p-3 bg-white">
                    <p className="text-sm font-medium text-neutral-800 truncate">
                      {file.originalName}
                    </p>
                    <div className="flex items-center justify-between text-xs text-neutral-500 mt-1">
                      <span>{formatFileSize(file.size)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-neutral-200">
            {filteredFiles.map((file) => (
              <div
                key={file.id}
                className="p-5 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedFiles.includes(file.id)}
                    onChange={() => handleSelectFile(file.id)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />

                  {/* Thumbnail */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 relative">
                    {file.type === 'video' ? (
                      <>
                        <video
                          src={file.url}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                          <FiPlay className="text-white" size={16} />
                        </div>
                      </>
                    ) : (
                      <img
                        src={file.url}
                        alt={file.altText || file.originalName}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-neutral-900 truncate">
                      {file.originalName}
                    </h3>
                    <div className="flex items-center space-x-3 text-sm text-neutral-600 mt-1">
                      <span>{formatFileSize(file.size)}</span>
                      <span>•</span>
                      <span>{formatDate(file.uploadedAt)}</span>
                      {file.dimensions && (
                        <>
                          <span>•</span>
                          <span>{file.dimensions.width}×{file.dimensions.height}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => window.open(file.url, '_blank')}
                      className="p-2 rounded-md text-neutral-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="View full size"
                    >
                      <FiEye size={16} />
                    </button>
                    
                    <button
                      onClick={() => navigator.clipboard.writeText(file.url)}
                      className="p-2 rounded-md text-neutral-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Copy URL"
                    >
                      <FiExternalLink size={16} />
                    </button>

                    <button
                      onClick={() => handleDeleteFile(file)}
                      className="p-2 rounded-md text-neutral-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete file"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  )
}

export default MediaLibrary